import React, { useState, useEffect, useRef } from "react";
import { Send, Users, MessageSquare, Search, Menu, X } from "lucide-react";
import io from "socket.io-client";

const Chat = () => {
  const userId = parseInt(sessionStorage.getItem("userId"));
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Initialize socket connection
    socketRef.current = io("https://api.made2tech.com", {
      transports: ["websocket"],
      autoConnect: true,
    });

    // Socket event listeners
    socketRef.current.on("connect", () => {
      console.log("Socket connected:", socketRef.current.id);
      if (selectedChat) {
        const chatRoomId = `techform_${selectedChat.id}`;
        socketRef.current.emit("join", {
          chat_id: chatRoomId,
          user_type: "user",
        });
      }
    });

    socketRef.current.on("push_notification", (data) => {
      console.log("Received notification:", data);
      if (data.type === "new_message") {
        const newMessage = {
          id: `msg-${Date.now()}`,
          content: data.message_preview,
          user_id: data.sender,
          timestamp: new Date().toISOString(),
          is_read: false,
        };

        if (selectedChat && `techform_${selectedChat.id}` === data.chat_id) {
          setMessages((prevMessages) => [...prevMessages, newMessage]);
        }

        setChats((prevChats) => {
          return prevChats.map((chat) => {
            if (`techform_${chat.id}` === data.chat_id) {
              return {
                ...chat,
                messages: [
                  ...(chat.messages || []),
                  { content: data.message_preview },
                ],
              };
            }
            return chat;
          });
        });
      }
    });

    socketRef.current.on("user_list", (users) => {
      console.log("Online users updated:", users);
      setOnlineUsers(new Set(users));
    });

    socketRef.current.on("chat_message", (data) => {
      console.log("Received message:", data);
      if (data.chat_id === `techform_${selectedChat?.id}`) {
        const newMsg = {
          id: `msg-${Date.now()}`,
          content: data.message,
          user_id: data.sender,
          timestamp: new Date().toISOString(),
          is_read: false,
        };
        setMessages((prev) => [...prev, newMsg]);
      }
    });

    socketRef.current.on("disconnect", () => {
      console.log("Socket disconnected");
    });

    // Cleanup on component unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [selectedChat]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Close sidebar on mobile when chat is selected
  useEffect(() => {
    if (selectedChat && window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  }, [selectedChat]);

  const fetchChats = async () => {
    try {
      const response = await fetch("https://api.made2tech.com/chat/3/list", {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setChats(data.chat);
    } catch (error) {
      console.error("Error fetching chats:", error);
      setError("Failed to load chats. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (chatId) => {
    try {
      const response = await fetch(
        `https://api.made2tech.com/techform/chats/${chatId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSelectedChat((prevChat) => ({
        ...prevChat,
        ...data,
        chat_name: prevChat.chat_name,
      }));

      const formattedMessages = data.messages.map((message) => ({
        ...message,
        main_user_id: parseInt(message.main_user_id),
        user_id: parseInt(message.user_id),
      }));

      setMessages(formattedMessages);
    } catch (error) {
      console.error("Error fetching messages:", error);
      setError("Failed to load messages. Please try again.");
    }
  };

  const handleChatSelection = async (chat) => {
    setSelectedChat({
      ...chat,
      chat_name: chat.chat_name,
    });
    await fetchMessages(chat.id);

    if (socketRef.current?.connected) {
      const chatRoomId = `techform_${chat.id}`;
      socketRef.current.emit("join", {
        chat_id: chatRoomId,
        user_type: "user",
      });
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || !socketRef.current?.connected)
      return;

    const messageData = {
      chat_id: `techform_${selectedChat.id}`,
      message: newMessage,
      user_id: userId,
    };

    try {
      // Send through socket
      socketRef.current.emit("chat_message", messageData);

      // Add message locally
      const localMessage = {
        id: `local-${Date.now()}`,
        content: newMessage,
        user_id: userId,
        timestamp: new Date().toISOString(),
        is_read: false,
      };
      setMessages((prev) => [...prev, localMessage]);
      setNewMessage("");

      // Send to API for persistence
      const response = await fetch(
        "https://api.made2tech.com/techform/messages/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({
            content: newMessage,
            chat_id: selectedChat.id,
            user_id: userId,
            main_user_id: userId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Update the local message with the server response if needed
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === `local-${Date.now()}` ? { ...data, user_id: userId } : msg
        )
      );
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Failed to send message. Please try again.");
      // Remove the local message if failed
      setMessages((prev) =>
        prev.filter((msg) => msg.id !== `local-${Date.now()}`)
      );
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else {
      return date.toLocaleDateString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  const getLastMessage = (chat) => {
    if (!chat.messages || chat.messages.length === 0) return "No messages";
    const lastMessage = chat.messages[chat.messages.length - 1];
    return lastMessage.content;
  };

  const getLastMessageTime = (chat) => {
    if (!chat.messages || chat.messages.length === 0) return "";
    const lastMessage = chat.messages[chat.messages.length - 1];
    return formatTimestamp(lastMessage.timestamp);
  };

  const filteredChats = chats.filter((chat) =>
    chat.chat_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderMessage = (message) => {
    const isCurrentUser = message.user_id === userId;
    const isOnline = onlineUsers.has(message.user_id.toString());

    // Message container with alignment
    return (
      <div
        key={message.id}
        className={`flex ${
          isCurrentUser ? "justify-end" : "justify-start"
        } mb-4`}
      >
        <div
          className={`
            max-w-[75%] rounded-lg p-3
            ${isCurrentUser ? "bg-[#24786D] text-white" : "bg-white shadow-sm"}
          `}
        >
          <p className="break-words">{message.content}</p>
          <div
            className={`text-xs mt-1 flex items-center ${
              isCurrentUser ? "text-gray-300" : "text-gray-500"
            }`}
          >
            <span>{formatTimestamp(message.timestamp)}</span>
            {isCurrentUser && (
              <>
                <span className="ml-2">✓</span>
                {isOnline && (
                  <span className="ml-1 w-2 h-2 bg-green-500 rounded-full" />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchChats();
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed md:relative w-80 h-full bg-white shadow-lg z-20
          transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
      >
        <div className="h-16 border-b flex items-center justify-between px-4">
          <h2 className="text-xl font-semibold text-[#24786D] flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Chats
          </h2>
          <button
            className="md:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setIsSidebarOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search chats..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#24786D] focus:border-[#24786D]"
            />
          </div>
        </div>

        <div className="overflow-y-auto h-[calc(100%-8rem)]">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#24786D]" />
            </div>
          ) : error ? (
            <div className="p-4 text-red-500">{error}</div>
          ) : (
            filteredChats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => {
                  handleChatSelection(chat);
                  setIsSidebarOpen(false);
                }}
                className={`
                  p-4 hover:bg-gray-50 cursor-pointer transition-colors
                  ${selectedChat?.id === chat.id ? "bg-gray-50" : ""}
                `}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#24786D] text-white flex items-center justify-center flex-shrink-0">
                    {chat.chat_name[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium truncate">
                        {chat.chat_name}
                      </span>
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {getLastMessageTime(chat)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {getLastMessage(chat)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-white relative">
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="h-16 border-b flex items-center px-4 bg-white">
              <button
                className="md:hidden mr-4 text-gray-500 hover:text-gray-700"
                onClick={() => setIsSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#24786D] text-white flex items-center justify-center">
                  {selectedChat.chat_name[0].toUpperCase()}
                </div>
                <div>
                  <h3 className="font-semibold">{selectedChat.chat_name}</h3>
                  <p className="text-sm text-gray-500">
                    {selectedChat.is_group ? "Group Chat" : "Direct Message"}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
              <div className="space-y-4">
                {messages.map((message) => renderMessage(message))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Message Input */}
            <div className="p-4 border-t bg-white">
              {error && (
                <div className="mb-2 text-sm text-red-500">{error}</div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && !e.shiftKey && sendMessage()
                  }
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#24786D] focus:border-[#24786D]"
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="bg-[#24786D] text-white p-2 rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-[#24786D]" />
              <p className="text-lg">Select a chat to start messaging</p>
              <p className="text-sm text-gray-400 mt-2">
                Choose from your conversations on the left
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;
